// Date utility functions - single source of truth for date/time manipulation
// CREATED: Utility functions for date formatting and manipulation
// UPDATED: Added time formatting functions (formatTimeToHHMM, calculateEndTime, formatTimeRange, formatDateShort)
// UPDATED: Consolidated date utilities from occurrences.ts (formatDate, parseDate, addDays, addMonths, addYears, etc.)
// UPDATED: Added formatRelativeDate for natural date display in lists ("Il y a 2h", "Hier", "3 janv.")

// ============================================================================
// DATE FORMATTING & PARSING
// ============================================================================

export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// ============================================================================
// DATE ARITHMETIC
// ============================================================================

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

export function addYears(date: Date, years: number): Date {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
}

export function adjustDayForMonth(day: number, month: number, year: number): number {
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
  return Math.min(day, lastDayOfMonth);
}

// ============================================================================
// DATE COMPARISON & DAY-OF-WEEK
// ============================================================================

export function getDayOfWeek(date: Date): number {
  const day = date.getDay();
  return day === 0 ? 7 : day;
}

export function isSameDay(date1: Date, date2: Date): boolean {
  return formatDate(date1) === formatDate(date2);
}

// ============================================================================
// TIME FORMATTING
// ============================================================================

export function normalizeTimeFormat(time: string): string {
  if (!time) return '';

  const parts = time.split(':');
  if (parts.length < 2) return '';

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return '';
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

export function formatTimeToHHMM(time: string | null, fallback: string = 'Non planifié'): string {
  if (!time) return fallback;
  return time.slice(0, 5);
}

export function calculateEndTime(startTime: string, durationMinutes: number): string {
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + durationMinutes;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
}

export function formatTimeRange(startTime: string, durationMinutes: number): string {
  const endTime = calculateEndTime(startTime, durationMinutes);
  return `De ${formatTimeToHHMM(startTime)} à ${formatTimeToHHMM(endTime)}`;
}

// ============================================================================
// DATE DISPLAY FORMATTING
// ============================================================================

export function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

export function formatDuration(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  if (hours === 0) {
    return `${mins}min`;
  }
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins.toString().padStart(2, '0')}min`;
}

// ============================================================================
// RELATIVE DATE FORMATTING
// ============================================================================

/**
 * Formats a date as a relative time string (e.g., "Il y a 2h", "Hier", "3 janv.")
 * More natural and scannable for list views
 */
export function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Less than 1 hour: "Il y a Xm"
  if (diffMinutes < 60) {
    if (diffMinutes < 1) return 'À l\'instant';
    return `Il y a ${diffMinutes}m`;
  }

  // Less than 24 hours: "Il y a Xh"
  if (diffHours < 24) {
    return `Il y a ${diffHours}h`;
  }

  // Yesterday
  if (diffDays === 1) {
    return 'Hier';
  }

  // Less than 7 days: "Il y a X jours"
  if (diffDays < 7) {
    return `Il y a ${diffDays} jours`;
  }

  // More than 7 days: "3 janv." or "3 janv. 2025" (if not current year)
  const currentYear = now.getFullYear();
  const dateYear = date.getFullYear();

  if (dateYear === currentYear) {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short'
    });
  } else {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }
}
